/**
 * Account-Tableau oben links: Avatar-Portrait, Spielername, Level.
 * Klick öffnet Dropdown-Menü (Account, Einstellungen, Logout).
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import { renderAvatarImage, AVATAR_PRESETS } from './AccountAvatarGrid';
import { fontSm, fontXs, padSm, padXs, gapSm, radius } from './UITheme';
import { getSettings, saveSettings } from './SettingsPanel';

const HOLO_BG = 'linear-gradient(180deg,rgba(8,20,45,0.93),rgba(4,12,28,0.9))';
const BRD = 'rgba(100,180,255,0.25)';
const ANIM_MS = 200;

export class PlayerBadge implements GameModule {
  readonly name = 'PlayerBadge';
  private _el!: HTMLDivElement;
  private _dropdown!: HTMLDivElement;
  private _avatarWrap!: HTMLDivElement;
  private _nameEl!: HTMLSpanElement;
  private _avatarId: number;
  private _open = false;
  private _onLogout: (() => void) | null;
  private _engine!: Engine;
  private _onOutside = (e: PointerEvent): void => {
    if (this._open && !this._el.contains(e.target as Node)) this._closeMenu();
  };

  constructor(
    private _playerName: string,
    onLogout: (() => void) | null = null,
    private _level: number = 1,
    avatarId: number = 0,
  ) {
    this._onLogout = onLogout;
    this._avatarId = avatarId;
  }

  init(engine: Engine): void {
    this._engine = engine;
    this._el = document.createElement('div');
    Object.assign(this._el.style, {
      position: 'fixed', top: padSm(), left: padSm(), zIndex: '9999',
      display: 'flex', alignItems: 'center', gap: gapSm(),
      background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(100,180,255,0.15)',
      borderRadius: radius(), padding: `${padXs()} ${padSm()}`,
      fontFamily: 'monospace', userSelect: 'none', cursor: 'pointer',
    });
    this._el.addEventListener('click', (e) => {
      e.stopPropagation();
      this._open ? this._closeMenu() : this._openMenu();
    });

    this._avatarWrap = document.createElement('div');
    this._avatarWrap.appendChild(this._buildAvatar());
    this._el.append(this._avatarWrap, this._buildInfoBlock());
    this._dropdown = this._buildDropdown();
    this._el.appendChild(this._dropdown);
    document.body.appendChild(this._el);
    document.addEventListener('pointerdown', this._onOutside);

    engine.events.on('planet:hub-opened', () => {
      this._closeMenu();
      this._el.style.display = 'none';
    });
    engine.events.on('planet:hub-closed', () => { this._el.style.display = 'flex'; });
    engine.events.on('account:avatar-changed', (ev: { avatarId: number }) => {
      this._avatarId = ev.avatarId;
      this._avatarWrap.innerHTML = '';
      this._avatarWrap.appendChild(this._buildAvatar());
    });
    engine.events.on('account:name-changed', (ev: { name: string }) => {
      this._playerName = ev.name;
      this._nameEl.textContent = ev.name;
    });
  }

  update(): void {}
  dispose(): void {
    document.removeEventListener('pointerdown', this._onOutside);
    this._el.remove();
  }

  private _buildAvatar(): HTMLDivElement {
    const size = 'clamp(28px, 7vw, 36px)';
    if (this._avatarId >= 0 && this._avatarId < AVATAR_PRESETS.length) {
      const av = renderAvatarImage(this._avatarId, size);
      av.style.border = '1.5px solid rgba(100,180,255,0.3)';
      return av;
    }
    const el = document.createElement('div');
    const hue = this._nameHash(this._playerName) % 360;
    Object.assign(el.style, {
      width: size, height: size, minWidth: size, borderRadius: '50%',
      background: `linear-gradient(135deg, hsl(${hue},60%,35%), hsl(${hue + 40},50%,25%))`,
      border: '1.5px solid rgba(100,180,255,0.3)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: fontSm(), color: 'rgba(255,255,255,0.85)',
      fontWeight: '700', letterSpacing: '0.5px', lineHeight: '1', textTransform: 'uppercase',
    });
    el.textContent = this._extractInitials(this._playerName);
    return el;
  }

  private _buildInfoBlock(): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { display: 'flex', flexDirection: 'column', gap: '1px' });
    this._nameEl = document.createElement('span');
    Object.assign(this._nameEl.style, {
      fontSize: fontSm(), color: '#cde', lineHeight: '1.2',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      maxWidth: 'clamp(80px, 22vw, 140px)',
    });
    this._nameEl.textContent = this._playerName;
    const lvEl = document.createElement('span');
    Object.assign(lvEl.style, {
      fontSize: fontXs(), color: 'rgba(100,180,255,0.7)', lineHeight: '1.2', letterSpacing: '0.5px',
    });
    lvEl.textContent = `LV ${this._level}`;
    wrap.append(this._nameEl, lvEl);
    return wrap;
  }

  private _settingsArea: HTMLDivElement | null = null;

  private _buildDropdown(): HTMLDivElement {
    const dd = document.createElement('div');
    Object.assign(dd.style, {
      position: 'absolute', top: '100%', left: '0', marginTop: '4px',
      background: HOLO_BG, border: `1px solid ${BRD}`, borderRadius: '8px',
      boxShadow: `0 4px 16px rgba(0,0,0,0.5), 0 0 12px rgba(80,160,255,0.08)`,
      minWidth: '200px', display: 'none', transformOrigin: 'top left',
      fontFamily: 'monospace', fontSize: fontSm(), overflow: 'hidden',
    });

    // Account
    dd.appendChild(this._menuRow('\uD83D\uDC64', 'Account', () => {
      this._closeMenu(); this._engine.events.emit('account:open', null);
    }));

    // Einstellungen (toggle inline)
    dd.appendChild(this._menuRow('\u2699\uFE0F', 'Einstellungen', () => this._toggleSettings()));
    this._settingsArea = document.createElement('div');
    Object.assign(this._settingsArea.style, { display: 'none', padding: '8px 14px' });
    dd.appendChild(this._settingsArea);

    // Separator + Abmelden
    const sep = document.createElement('div');
    sep.style.cssText = `height:1px;background:${BRD};`;
    dd.appendChild(sep);
    dd.appendChild(this._menuRow('\uD83D\uDEAA', 'Abmelden', () => {
      this._closeMenu(); this._onLogout?.();
    }));

    return dd;
  }

  private _menuRow(icon: string, label: string, action: () => void): HTMLDivElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      padding: '10px 14px', cursor: 'pointer', color: '#8cf',
      display: 'flex', alignItems: 'center', gap: '10px', transition: 'background 0.15s',
    });
    row.innerHTML = `<span>${icon}</span><span>${label}</span>`;
    row.addEventListener('pointerenter', () => { row.style.background = 'rgba(100,180,255,0.08)'; });
    row.addEventListener('pointerleave', () => { row.style.background = 'transparent'; });
    row.addEventListener('click', (e) => { e.stopPropagation(); action(); });
    return row;
  }

  private _toggleSettings(): void {
    const area = this._settingsArea!;
    if (area.style.display !== 'none') { area.style.display = 'none'; return; }
    area.style.display = '';
    area.innerHTML = '';
    const s = getSettings();
    area.appendChild(this._buildSlider('Scroll-Speed', s.panSpeed, 0.3, 5.0, 0.1, (v) => {
      s.panSpeed = v; saveSettings();
    }));
  }

  private _buildSlider(label: string, value: number, min: number, max: number, step: number, onChange: (v: number) => void): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.style.marginBottom = '4px';
    const lbl = document.createElement('div');
    lbl.style.cssText = 'display:flex;justify-content:space-between;margin-bottom:3px;';
    const nameEl = document.createElement('span');
    nameEl.textContent = label; nameEl.style.color = '#7ab8ff'; nameEl.style.fontSize = fontXs();
    const valEl = document.createElement('span');
    valEl.textContent = value.toFixed(1); valEl.style.color = '#aaa'; valEl.style.fontSize = fontXs();
    lbl.append(nameEl, valEl);
    const slider = document.createElement('input');
    slider.type = 'range'; slider.min = String(min); slider.max = String(max);
    slider.step = String(step); slider.value = String(value);
    Object.assign(slider.style, { width: '100%', accentColor: '#4a8aff', height: '20px' });
    slider.addEventListener('input', (e) => {
      e.stopPropagation();
      const v = parseFloat(slider.value);
      valEl.textContent = v.toFixed(1); onChange(v);
    });
    slider.addEventListener('click', (e) => e.stopPropagation());
    slider.addEventListener('pointerdown', (e) => e.stopPropagation());
    wrap.append(lbl, slider);
    return wrap;
  }

  private _openMenu(): void {
    this._open = true;
    this._dropdown.style.display = '';
    this._dropdown.animate(
      [{ transform: 'scaleY(0)', opacity: 0 }, { transform: 'scaleY(1)', opacity: 1 }],
      { duration: ANIM_MS, easing: 'cubic-bezier(0.22,0.85,0.32,1.1)', fill: 'forwards' },
    );
  }

  private _closeMenu(): void {
    if (!this._open) return;
    this._open = false;
    const a = this._dropdown.animate(
      [{ transform: 'scaleY(1)', opacity: 1 }, { transform: 'scaleY(0)', opacity: 0 }],
      { duration: ANIM_MS * 0.6, easing: 'ease-in', fill: 'forwards' },
    );
    a.onfinish = () => { this._dropdown.style.display = 'none'; };
  }

  private _extractInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return parts[0][0] + parts[1][0];
    return name.slice(0, 2);
  }

  private _nameHash(name: string): number {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return Math.abs(h);
  }
}
